// Checks a flow graph before it is saved or published. Graphs arrive as JSON
// from the canvas and the API, so this checks shape as well as meaning; a
// graph that passes is safe to hand to interpret.ts.
//
// Errors block publishing. Warnings (an unreachable node) don't.

import { VARIABLE_NAME_RE } from './template';
import { FLOW_GRAPH_VERSION, type ConditionOp, type FlowGraph, type FlowNode, type FlowNodeType } from './types';

export const FLOW_LIMITS = {
  nodes: 300,
  idLength: 64,
  text: 4000,
  options: 10,
  optionLength: 100,
  tagLength: 50,
  urlLength: 2000,
  headers: 20,
  attributeValue: 500,
  /** A delay or question timeout: 1 second to 30 days. */
  minWaitMs: 1000,
  maxWaitMs: 30 * 24 * 60 * 60 * 1000,
} as const;

export interface FlowIssue {
  nodeId?: string;
  field?: string;
  message: string;
}

export type FlowValidation =
  | { ok: true; graph: FlowGraph; warnings: FlowIssue[] }
  | { ok: false; errors: FlowIssue[]; warnings: FlowIssue[] };

const NODE_TYPES: readonly FlowNodeType[] = [
  'trigger', 'message', 'question', 'condition', 'delay', 'tag', 'set_attribute', 'assign', 'close', 'webhook', 'rest',
];

/** A customer attribute's key: lower-case, so `contact.plan` is one name. */
export const ATTRIBUTE_KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
/** Keys the contact already has as columns (`contact.name` etc. come from
 *  them), so an attribute can't shadow one. */
export const RESERVED_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set(['name', 'email', 'phone', 'id']);
const OPS: readonly ConditionOp[] = ['eq', 'neq', 'contains', 'not_contains', 'exists', 'not_exists', 'gt', 'lt', 'in'];
const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
/** Nodes that pause the run. A loop must pass through one. */
const WAITING: ReadonlySet<FlowNodeType> = new Set(['question', 'delay', 'rest']);

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === 'string';

/** The node ids a node can go to next, with the field each comes from. */
export function nodeTargets(node: FlowNode): Array<{ field: string; target: string | null }> {
  switch (node.type) {
    case 'close':
      return [];
    case 'condition':
      return [...node.rules.map((r, i) => ({ field: `rules.${i}.next`, target: r.next })), { field: 'else', target: node.else }];
    case 'question':
      return [{ field: 'next', target: node.next }, ...(node.onTimeout !== undefined ? [{ field: 'onTimeout', target: node.onTimeout }] : [])];
    case 'rest':
      return [{ field: 'next', target: node.next }, ...(node.onError !== undefined ? [{ field: 'onError', target: node.onError }] : [])];
    default:
      return [{ field: 'next', target: node.next }];
  }
}

export function validateFlowGraph(input: unknown): FlowValidation {
  const errors: FlowIssue[] = [];
  const warnings: FlowIssue[] = [];
  const err = (message: string, nodeId?: string, field?: string) => errors.push({ message, nodeId, field });

  if (!isObj(input)) return { ok: false, errors: [{ message: 'graph must be an object' }], warnings };
  if (input.version !== FLOW_GRAPH_VERSION) err(`version must be ${FLOW_GRAPH_VERSION}`);
  if (!Array.isArray(input.nodes)) return { ok: false, errors: [...errors, { message: 'nodes must be a list' }], warnings };
  if (input.nodes.length > FLOW_LIMITS.nodes) err(`at most ${FLOW_LIMITS.nodes} nodes`);

  const ids = new Set<string>();
  for (const [i, raw] of input.nodes.entries()) {
    if (!isObj(raw) || !isStr(raw.id) || raw.id === '' || raw.id.length > FLOW_LIMITS.idLength) {
      err(`node ${i}: id must be 1–${FLOW_LIMITS.idLength} characters`);
      continue;
    }
    if (ids.has(raw.id)) err('duplicate node id', raw.id);
    ids.add(raw.id);
    if (!NODE_TYPES.includes(raw.type as FlowNodeType)) {
      err(`unknown node type ${JSON.stringify(raw.type)}`, raw.id, 'type');
      continue;
    }
    checkNode(raw, err);
  }
  if (errors.length > 0) return { ok: false, errors, warnings };

  // Shape is right from here on.
  const graph = input as unknown as FlowGraph;
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));

  const triggers = graph.nodes.filter((n) => n.type === 'trigger');
  if (triggers.length !== 1) err(`a flow needs exactly one trigger (has ${triggers.length})`);

  for (const node of graph.nodes) {
    for (const { field, target } of nodeTargets(node)) {
      if (target === null) continue;
      const to = byId.get(target);
      if (!to) err(`goes to missing node ${JSON.stringify(target)}`, node.id, field);
      else if (to.type === 'trigger') err('cannot go back to the trigger', node.id, field);
    }
  }

  // A cycle made only of nodes that don't wait would spin without pausing.
  const loop = findInstantLoop(graph, byId);
  if (loop) err(`loop with no question, delay or REST call in it: ${loop.join(' → ')}`, loop[0]);

  if (triggers.length === 1) {
    const reached = reachable(triggers[0]!.id, byId);
    for (const n of graph.nodes) {
      if (!reached.has(n.id)) warnings.push({ nodeId: n.id, message: 'not connected to the trigger; it never runs' });
    }
  }

  return errors.length > 0 ? { ok: false, errors, warnings } : { ok: true, graph, warnings };
}

type Report = (message: string, nodeId?: string, field?: string) => void;

function checkNode(n: Obj, err: Report): void {
  const id = n.id as string;
  const target = (field: string, optional = false) => {
    const v = n[field];
    if (optional && v === undefined) return;
    if (v !== null && !isStr(v)) err('must be a node id or null', id, field);
  };
  const text = (field: string) => {
    const v = n[field];
    if (!isStr(v) || v.trim() === '') err('must not be empty', id, field);
    else if (v.length > FLOW_LIMITS.text) err(`at most ${FLOW_LIMITS.text} characters`, id, field);
  };
  const waitMs = (field: string, optional = false) => {
    const v = n[field];
    if (optional && v === undefined) return;
    if (typeof v !== 'number' || !Number.isInteger(v) || v < FLOW_LIMITS.minWaitMs || v > FLOW_LIMITS.maxWaitMs) {
      err('must be between 1 second and 30 days', id, field);
    }
  };
  const variable = (v: unknown, field: string) => {
    if (!isStr(v) || !VARIABLE_NAME_RE.test(v)) err('must be a variable name like answer or contact.email', id, field);
  };
  const stringList = (field: string, max: number, maxLen: number) => {
    const v = n[field];
    if (v === undefined) return;
    if (!Array.isArray(v) || v.length > max || !v.every((x) => isStr(x) && x.trim() !== '' && x.length <= maxLen)) {
      err(`must be up to ${max} non-empty items of at most ${maxLen} characters`, id, field);
    }
  };
  if (n.position !== undefined) {
    const p = n.position;
    if (!isObj(p) || typeof p.x !== 'number' || typeof p.y !== 'number') err('must be { x, y }', id, 'position');
  }

  switch (n.type as FlowNodeType) {
    case 'trigger': {
      const t = n.trigger;
      const kinds = ['conversation_started', 'message_received', 'manual'];
      if (!isObj(t) || !kinds.includes(t.kind as string)) err(`must be one of ${kinds.join(', ')}`, id, 'trigger');
      else if (t.kind === 'message_received' && t.keywords !== undefined) {
        if (!Array.isArray(t.keywords) || !t.keywords.every((k) => isStr(k) && k.trim() !== '')) {
          err('keywords must be non-empty text', id, 'trigger.keywords');
        }
      }
      target('next');
      break;
    }
    case 'message':
      text('text');
      target('next');
      break;
    case 'question':
      text('text');
      variable(n.variable, 'variable');
      stringList('options', FLOW_LIMITS.options, FLOW_LIMITS.optionLength);
      waitMs('timeoutMs', true);
      target('next');
      target('onTimeout', true);
      break;
    case 'condition':
      if (!Array.isArray(n.rules)) err('must be a list', id, 'rules');
      else {
        n.rules.forEach((r, i) => {
          if (!isObj(r) || !Array.isArray(r.when) || r.when.length === 0) {
            err('each rule needs at least one condition', id, `rules.${i}`);
            return;
          }
          if (r.next !== null && !isStr(r.next)) err('must be a node id or null', id, `rules.${i}.next`);
          r.when.forEach((c, j) => checkCondition(c, `rules.${i}.when.${j}`, id, err));
        });
      }
      target('else');
      break;
    case 'delay':
      waitMs('ms');
      target('next');
      break;
    case 'tag':
      stringList('add', 20, FLOW_LIMITS.tagLength);
      stringList('remove', 20, FLOW_LIMITS.tagLength);
      if (!(Array.isArray(n.add) && n.add.length) && !(Array.isArray(n.remove) && n.remove.length)) {
        err('add or remove at least one tag', id);
      }
      target('next');
      break;
    case 'set_attribute':
      if (!isStr(n.key) || !ATTRIBUTE_KEY_RE.test(n.key)) {
        err('must be lower-case letters, digits or _ (up to 40), starting with a letter', id, 'key');
      } else if (RESERVED_ATTRIBUTE_KEYS.has(n.key)) {
        err(`${n.key} is a built-in contact field`, id, 'key');
      }
      if (!isStr(n.value) || n.value.trim() === '') err('must not be empty', id, 'value');
      else if (n.value.length > FLOW_LIMITS.attributeValue) err(`at most ${FLOW_LIMITS.attributeValue} characters`, id, 'value');
      target('next');
      break;
    case 'assign': {
      const to = n.to;
      const ok = isObj(to) && (to.kind === 'unassigned' || (to.kind === 'agent' && isStr(to.agentId) && to.agentId !== ''));
      if (!ok) err('must be an agent or unassigned', id, 'to');
      target('next');
      break;
    }
    case 'close':
      break;
    case 'webhook':
      checkUrl(n.url, id, err);
      target('next');
      break;
    case 'rest':
      if (!METHODS.includes(n.method as string)) err(`must be one of ${METHODS.join(', ')}`, id, 'method');
      checkUrl(n.url, id, err);
      if (n.headers !== undefined) {
        const h = n.headers;
        if (!isObj(h) || Object.keys(h).length > FLOW_LIMITS.headers || !Object.values(h).every(isStr)) {
          err(`must be up to ${FLOW_LIMITS.headers} text headers`, id, 'headers');
        }
      }
      if (n.body !== undefined && (!isStr(n.body) || n.body.length > FLOW_LIMITS.text)) {
        err(`must be text of at most ${FLOW_LIMITS.text} characters`, id, 'body');
      }
      if (n.save !== undefined) {
        if (!isObj(n.save)) err('must map variable names to response paths', id, 'save');
        else {
          for (const [name, path] of Object.entries(n.save)) {
            variable(name, `save.${name}`);
            if (!isStr(path) || path === '') err('must be a response path like order.status', id, `save.${name}`);
          }
        }
      }
      target('next');
      target('onError', true);
      break;
  }
}

function checkCondition(c: unknown, field: string, id: string, err: Report): void {
  if (!isObj(c) || !isStr(c.var) || !VARIABLE_NAME_RE.test(c.var)) {
    err('needs a variable name', id, `${field}.var`);
    return;
  }
  if (!OPS.includes(c.op as ConditionOp)) {
    err(`operator must be one of ${OPS.join(', ')}`, id, `${field}.op`);
    return;
  }
  const v = c.value;
  switch (c.op as ConditionOp) {
    case 'exists':
    case 'not_exists':
      return;
    case 'in':
      if (!Array.isArray(v) || v.length === 0 || !v.every(isStr)) err('needs a list of values', id, `${field}.value`);
      return;
    case 'gt':
    case 'lt':
      if (!(typeof v === 'number' || (isStr(v) && v.trim() !== '' && Number.isFinite(Number(v))))) {
        err('needs a number', id, `${field}.value`);
      }
      return;
    default:
      if (!isStr(v) && typeof v !== 'number') err('needs a value', id, `${field}.value`);
  }
}

/** http(s) only, and the scheme and host must be written out: a variable
 *  (visitor input) may fill in the path or query, never choose the server.
 *  The server still SSRF-checks the final URL before calling it. */
function checkUrl(v: unknown, id: string, err: Report): void {
  if (!isStr(v) || v.length > FLOW_LIMITS.urlLength) {
    err('must be a URL', id, 'url');
    return;
  }
  const origin = /^(https?):\/\/([^/?#]*)/i.exec(v);
  if (!origin) {
    err('must start with http:// or https://', id, 'url');
    return;
  }
  if (origin[2]!.includes('{{') || origin[2] === '') {
    err('the host must be written out, not taken from a variable', id, 'url');
    return;
  }
  try {
    new URL(v.replace(/\{\{[^}]*\}\}/g, 'x'));
  } catch {
    err('is not a valid URL', id, 'url');
  }
}

function reachable(start: string, byId: Map<string, FlowNode>): Set<string> {
  const seen = new Set<string>();
  const stack = [start];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    const node = byId.get(id);
    if (!node) continue;
    seen.add(id);
    for (const { target } of nodeTargets(node)) if (target !== null) stack.push(target);
  }
  return seen;
}

/** A cycle through non-waiting nodes only, as a list of ids, or null. */
function findInstantLoop(graph: FlowGraph, byId: Map<string, FlowNode>): string[] | null {
  const state = new Map<string, 'visiting' | 'done'>();
  const path: string[] = [];
  const visit = (id: string): string[] | null => {
    const node = byId.get(id);
    if (!node || WAITING.has(node.type)) return null;
    const s = state.get(id);
    if (s === 'done') return null;
    if (s === 'visiting') return [...path.slice(path.indexOf(id)), id];
    state.set(id, 'visiting');
    path.push(id);
    for (const { target } of nodeTargets(node)) {
      if (target === null) continue;
      const found = visit(target);
      if (found) return found;
    }
    path.pop();
    state.set(id, 'done');
    return null;
  };
  for (const n of graph.nodes) {
    const found = visit(n.id);
    if (found) return found;
  }
  return null;
}
